import { CHECKOUT_SDK_ERROR_CODES } from '@ekhodealer/ekho-common';

import type { CheckoutSdkErrorCode } from '@ekhodealer/ekho-common';

/**
 * Typed error thrown by all checkout SDK methods. Integrators catch and
 * switch on `error.code` for programmatic error handling.
 */
export class CheckoutSdkError extends Error {
  readonly code: CheckoutSdkErrorCode;
  readonly details?: Record<string, unknown>;

  constructor(message: string, code: CheckoutSdkErrorCode, details?: Record<string, unknown>) {
    super(message);
    this.name = 'CheckoutSdkError';
    this.code = code;
    this.details = details;
  }
}

/** Structured error body emitted by the SDK BFF on non-2xx responses. */
export interface SdkErrorBody {
  code?: string;
  message?: string;
  details?: Record<string, unknown>;
}

const VALID_CODES: ReadonlySet<string> = new Set(Object.values(CHECKOUT_SDK_ERROR_CODES));

function isValidCode(value: unknown): value is CheckoutSdkErrorCode {
  return typeof value === 'string' && VALID_CODES.has(value);
}

/** Map HTTP status codes to CheckoutSdkErrorCode. */
function errorCodeFromStatus(status: number): CheckoutSdkErrorCode {
  if (status === 422 || status === 400) return CHECKOUT_SDK_ERROR_CODES.VALIDATION_ERROR;
  if (status === 429) return CHECKOUT_SDK_ERROR_CODES.RATE_LIMITED;
  if (status === 404) return CHECKOUT_SDK_ERROR_CODES.NOT_FOUND;
  if (status === 401 || status === 403) return CHECKOUT_SDK_ERROR_CODES.UNAUTHORIZED;
  return CHECKOUT_SDK_ERROR_CODES.SERVER_ERROR;
}

/**
 * Prefer the server-supplied `code` when present and valid, otherwise derive
 * from the HTTP status. Keeps dealer-page `switch (err.code)` working against
 * both new servers (typed code) and old/proxy responses (status only).
 */
export function errorCodeFromBodyOrStatus(
  body: SdkErrorBody | undefined,
  status: number,
): CheckoutSdkErrorCode {
  if (body && isValidCode(body.code)) return body.code;
  return errorCodeFromStatus(status);
}
